import React, { useState } from "react";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Download, Upload, FileSpreadsheet, AlertTriangle, CheckCircle2 } from "lucide-react";

/**
 * Phase 6.2 — Bulk incentive setup workflow.
 *
 *  PM side:
 *    1. Download the all-products setup template (current regular + self incentives are pre-filled).
 *    2. Fill `proposed_regular_promoter_incentive` and/or `proposed_self_promoter_incentive`
 *       + `effective_from_bs` per row.
 *    3. Upload → preview (insert/update/skip/error counts) → confirm → submitted as
 *       pending_admin_approval proposals.
 *
 *  Admin side:
 *    1. Export pending proposals.
 *    2. Fill admin_decision = approve | reject | no_change (rejection requires admin_remarks).
 *    3. Upload → preview → confirm → bulk approve/reject in one batch.
 */
export default function BulkIncentiveSetup() {
  const { can } = useAuth();
  const canPropose = can("promoter_financial.propose");
  const canApprove = can("promoter_financial.approve");
  return (
    <div className="space-y-5" data-testid="bulk-incentive-page">
      <header>
        <h1 className="font-heading text-3xl font-bold text-slate-900">Bulk Incentive Setup</h1>
        <p className="text-sm text-slate-500 mt-1">
          Phase 6.2 — Excel-driven workflow for setting up incentive rules for all products in one go.
          Separate columns for regular and self-promoter incentives. Admin approval still required.
        </p>
      </header>
      {canPropose && <PMSection />}
      {canApprove && <AdminSection />}
      {!canPropose && !canApprove && (
        <Card><CardContent className="p-6 text-center text-slate-500">
          You don&apos;t have permission to propose or approve incentive rules.
        </CardContent></Card>
      )}
    </div>
  );
}

function PMSection() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const downloadTemplate = async (path, fname) => {
    const token = localStorage.getItem("access_token");
    const r = await fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) { toast.error("Could not download"); return; }
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = fname; a.click();
    URL.revokeObjectURL(url);
  };
  const handleFile = (e) => { setFile(e.target.files?.[0] || null); setPreview(null); };
  const previewBulk = async () => {
    if (!file) { toast.error("Choose a file first"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/promoter/financial/incentive-rules/bulk-preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
    } catch (e) {
      toast.error("Preview failed", { description: formatApiErrorDetail(e.response?.data?.detail) });
    } finally { setBusy(false); }
  };
  const submitBulk = async () => {
    if (!file) { toast.error("Choose a file first"); return; }
    if (!preview) { toast.error("Preview before submitting"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/promoter/financial/incentive-rules/bulk-upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Submitted ${data.summary.created_proposals} proposals for admin approval`);
      setFile(null); setPreview(null);
    } catch (e) {
      toast.error("Upload failed", { description: formatApiErrorDetail(e.response?.data?.detail) });
    } finally { setBusy(false); }
  };
  return (
    <Card data-testid="bulk-pm-section">
      <CardContent className="p-4 space-y-3">
        <div className="flex justify-between items-start flex-wrap gap-2">
          <div>
            <h2 className="font-semibold text-slate-900">Product Manager · Propose Incentives in Bulk</h2>
            <p className="text-xs text-slate-500 mt-1">Step 1: download template · Step 2: fill in Excel · Step 3: preview &amp; submit</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm"
                    onClick={() => downloadTemplate("/promoter/financial/incentive-rules/template.xlsx", "incentive_setup_template.xlsx")}
                    data-testid="bulk-template-download">
              <FileSpreadsheet size={14} className="mr-1" /> Download Template
            </Button>
            <Button variant="outline" size="sm"
                    onClick={() => downloadTemplate("/promoter/financial/incentive-rules/active.xlsx", "incentive_active_rules.xlsx")}
                    data-testid="bulk-active-download">
              <Download size={14} className="mr-1" /> Active Rules
            </Button>
            <Button variant="outline" size="sm"
                    onClick={() => downloadTemplate("/promoter/financial/incentive-rules/self-promoter.xlsx", "incentive_self_promoter_rules.xlsx")}
                    data-testid="bulk-self-download">
              <Download size={14} className="mr-1" /> Self-Promoter Rules
            </Button>
          </div>
        </div>
        <div className="flex items-end gap-2 flex-wrap pt-2 border-t border-slate-100">
          <div>
            <Label className="text-[11px] uppercase font-semibold text-slate-600">Upload completed file</Label>
            <Input type="file" accept=".xlsx" onChange={handleFile} className="h-9" data-testid="bulk-pm-file" />
          </div>
          <Button variant="outline" disabled={!file || busy} onClick={previewBulk} data-testid="bulk-pm-preview">
            Preview
          </Button>
          <Button disabled={!preview || busy} onClick={submitBulk} className="bg-slate-900 hover:bg-slate-800 text-white"
                  data-testid="bulk-pm-submit">
            <Upload size={14} className="mr-1" /> Submit Proposals
          </Button>
        </div>
        {preview && <PreviewSummary p={preview} role="pm" />}
      </CardContent>
    </Card>
  );
}

function AdminSection() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const downloadPending = async () => {
    const token = localStorage.getItem("access_token");
    const r = await fetch(`${API}/promoter/financial/proposals/pending.xlsx`, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) { toast.error("Could not download"); return; }
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "pending_incentive_proposals.xlsx"; a.click();
    URL.revokeObjectURL(url);
  };
  const previewBulk = async () => {
    if (!file) { toast.error("Choose a file first"); return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/promoter/financial/proposals/bulk-decide-preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
    } catch (e) {
      toast.error("Preview failed", { description: formatApiErrorDetail(e.response?.data?.detail) });
    } finally { setBusy(false); }
  };
  const submitBulk = async () => {
    if (!file || !preview) { toast.error("Preview before submitting"); return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/promoter/financial/proposals/bulk-decide", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Bulk decision applied — ${data.summary.approved} approved, ${data.summary.rejected} rejected`);
      setFile(null); setPreview(null);
    } catch (e) {
      toast.error("Apply failed", { description: formatApiErrorDetail(e.response?.data?.detail) });
    } finally { setBusy(false); }
  };
  return (
    <Card data-testid="bulk-admin-section">
      <CardContent className="p-4 space-y-3">
        <div className="flex justify-between items-start flex-wrap gap-2">
          <div>
            <h2 className="font-semibold text-slate-900">Admin · Bulk Approve / Reject Pending Proposals</h2>
            <p className="text-xs text-slate-500 mt-1">Step 1: download pending list · Step 2: fill admin_decision &amp; admin_remarks · Step 3: preview &amp; apply</p>
          </div>
          <Button variant="outline" size="sm" onClick={downloadPending} data-testid="bulk-admin-download" data-testid-alias="bulk-pending-download">
            <Download size={14} className="mr-1" /> Pending Proposals
          </Button>
        </div>
        <div className="flex items-end gap-2 flex-wrap pt-2 border-t border-slate-100">
          <div>
            <Label className="text-[11px] uppercase font-semibold text-slate-600">Upload decided file</Label>
            <Input type="file" accept=".xlsx" onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }} className="h-9" data-testid="bulk-admin-file" />
          </div>
          <Button variant="outline" disabled={!file || busy} onClick={previewBulk} data-testid="bulk-admin-preview">Preview</Button>
          <Button disabled={!preview || busy} onClick={submitBulk} className="bg-slate-900 hover:bg-slate-800 text-white"
                  data-testid="bulk-admin-submit">
            <Upload size={14} className="mr-1" /> Apply Decisions
          </Button>
        </div>
        {preview && <PreviewSummary p={preview} role="admin" />}
      </CardContent>
    </Card>
  );
}

function PreviewSummary({ p, role }) {
  const s = p.summary || {};
  return (
    <div className="bg-slate-50 rounded p-3 space-y-2 text-sm" data-testid="bulk-preview-summary">
      <div className="flex gap-3 flex-wrap text-xs">
        {role === "pm" && (
          <>
            <Tag color="emerald" label={`${s.to_insert ?? 0} new`} testid="preview-insert" />
            <Tag color="amber" label={`${s.to_revise ?? 0} revisions`} testid="preview-update" />
            <Tag color="slate" label={`${s.skipped_no_change ?? 0} unchanged`} testid="preview-skip" />
            <Tag color="rose" label={`${s.failed_rows ?? 0} errors`} testid="preview-fail" />
          </>
        )}
        {role === "admin" && (
          <>
            <Tag color="emerald" label={`${s.to_approve ?? 0} approve`} testid="preview-approve" />
            <Tag color="rose" label={`${s.to_reject ?? 0} reject`} testid="preview-reject" />
            <Tag color="slate" label={`${s.skipped ?? 0} skipped`} testid="preview-skip" />
            <Tag color="amber" label={`${s.failed_rows ?? 0} errors`} testid="preview-fail" />
          </>
        )}
      </div>
      {(p.errors || []).length > 0 && (
        <div className="text-xs text-rose-700">
          <div className="font-semibold flex items-center gap-1"><AlertTriangle size={12} /> Row errors</div>
          <ul className="list-disc pl-4 max-h-32 overflow-auto">
            {(p.errors || []).slice(0, 50).map((e, i) => (
              <li key={i}>Row {e.row}: {e.error}{e.proposal_id ? ` (proposal ${e.proposal_id.slice(0,8)})` : ""}</li>
            ))}
          </ul>
        </div>
      )}
      {s.failed_rows === 0 && (
        <div className="text-xs text-emerald-700 flex items-center gap-1">
          <CheckCircle2 size={12} /> All rows parsed successfully — click {role === "admin" ? "Apply Decisions" : "Submit Proposals"} to confirm.
        </div>
      )}
    </div>
  );
}

function Tag({ color, label, testid }) {
  const map = {
    emerald: "bg-emerald-100 text-emerald-800",
    amber: "bg-amber-100 text-amber-800",
    rose: "bg-rose-100 text-rose-800",
    slate: "bg-slate-100 text-slate-700",
  };
  return <Badge className={map[color]} data-testid={testid}>{label}</Badge>;
}
